/** What you are pointing at, shown as the card you already know.
 *
 * The atlas used to answer a hover by printing the node's name on the canvas,
 * in world space, wherever the node happened to sit. That is the worst place
 * for text: it lands on top of other nodes, it flickers as the cursor crosses
 * a cluster, and a name on its own does not tell you which note it is anyway —
 * a vault has six "Untitled note"s.
 *
 * The side panel does answer it properly, but it is collapsed most of the time,
 * and opening a panel is a bigger commitment than pointing at a dot.
 *
 * So the hover answer is the HOMEPAGE'S CARD: the same preview, the same folder
 * breadcrumb, the same description and tags, from the same builder on the
 * server. One picture of a note, in both places. It floats above the canvas
 * near the cursor, never under it, and it is `pointer-events-none` throughout —
 * a card that could be hovered would steal the pointer from the graph and
 * fight the thing that summoned it.
 */
import * as React from "react";

import { FolderBreadcrumb } from "@/components/home/FolderBreadcrumb";
import { NotePreview, isLightSurface } from "@/components/home/NotePreview";
import { useNoteCard } from "@/hooks/queries";
import type { GNode } from "@/stores/graph";
import { cn } from "@/lib/utils";

/** Card box, in screen px. Narrower than a homepage card: it is a glance, and
 *  a wide panel following the cursor covers the graph you are reading. */
const W = 248;
const H_MAX = 300;
/** Clearance from the cursor, so the card never sits under the pointer. */
const GAP = 18;

/** Which feed card belongs to this node.
 *
 * A document node stands for a document; every other node type IS the thing.
 * Sections and fragments have no card of their own — they are parts of a
 * document, not notes — so they get the lightweight fallback below rather than
 * a request that would come back empty. */
function refFor(node: GNode): string | null {
  if (node.type === "document") return node.document_id ? `document:${node.document_id}` : null;
  if (node.type === "section" || node.type === "fragment") return null;
  return `node:${node.id}`;
}

export function NodeHoverCard({
  node,
  x,
  y,
  bounds,
}: {
  node: GNode;
  /** Cursor position, in coordinates relative to the atlas container. */
  x: number;
  y: number;
  bounds: { width: number; height: number };
}) {
  const ref = refFor(node);
  const card = useNoteCard(ref);

  // Flip to whichever side has room, and clamp vertically, so the card is
  // always whole. A card that runs off the edge is worse than no card: you
  // learn that something exists and cannot read it.
  const flipLeft = x + GAP + W > bounds.width;
  const left = flipLeft ? Math.max(8, x - GAP - W) : x + GAP;
  const top = Math.min(Math.max(8, y - 40), Math.max(8, bounds.height - H_MAX - 8));

  const data = card.data;
  const onLightPlate =
    data?.preview.mode === "card" && isLightSurface(data.preview.surface?.background);

  return (
    <div
      // Not aria-live and not focusable: this is a visual echo of the pointer,
      // and a keyboard or screen-reader user reaches the same facts through the
      // context panel, which is built for reading rather than glancing.
      aria-hidden
      className="pointer-events-none absolute z-30 animate-rise-in"
      style={{ left, top, width: W }}
    >
      <div className="overflow-hidden rounded-lg bg-card shadow-[0_24px_64px_-18px_rgba(0,0,0,0.92)] ring-1 ring-border">
        {data ? (
          <>
            <div className="relative h-[108px] w-full shrink-0">
              <div className="h-full w-full">
                <NotePreview card={data} />
              </div>
              {data.preview.mode === "image" && data.preview.url && (
                <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-black/70 via-black/35 to-transparent" />
              )}
              <FolderBreadcrumb
                card={data}
                tone={onLightPlate ? "light" : "dark"}
                className="absolute inset-x-0 top-0 px-2.5 pt-2"
              />
            </div>
            <div className="flex flex-col gap-1 px-2.5 pb-2.5 pt-2">
              <h3 className="line-clamp-2 font-display text-[13.5px] font-medium leading-[1.25] text-foreground">
                {data.title}
              </h3>
              {data.description && (
                <p className="line-clamp-3 text-[11px] leading-[1.5] text-muted-foreground">
                  {data.description}
                </p>
              )}
              {data.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {data.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="max-w-full truncate rounded-full border border-border px-1.5 py-px font-mono text-[9px] text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          // No card: either this node has none (a section, a fragment) or one
          // is still in flight. Show the node's own name at the size the title
          // will be, so the card does not resize under the cursor when the
          // real one lands.
          <div className="flex flex-col gap-1 px-2.5 py-2.5">
            <h3
              className={cn(
                "line-clamp-2 font-display text-[13.5px] font-medium leading-[1.25] text-foreground",
                ref && card.isLoading && "opacity-60",
              )}
            >
              {node.label}
            </h3>
            {node.summary && (
              <p className="line-clamp-3 text-[11px] leading-[1.5] text-muted-foreground">
                {node.summary}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Follow the pointer without re-rendering the atlas on every mousemove.
 *
 * `onNodeHover` gives the node but not the cursor, and putting the cursor in
 * React state would re-render the graph host at pointer rate. The position is
 * read from a ref that a plain DOM listener writes, and only a CHANGE OF NODE
 * moves the card — so it appears where the pointer entered the node and stays
 * put while you sit on it, which is also steadier to read.
 */
export function useHoverPoint(host: React.RefObject<HTMLElement | null>) {
  const point = React.useRef({ x: 0, y: 0 });
  React.useEffect(() => {
    const el = host.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      const box = el.getBoundingClientRect();
      point.current = { x: e.clientX - box.left, y: e.clientY - box.top };
    };
    el.addEventListener("pointermove", onMove, { passive: true });
    return () => el.removeEventListener("pointermove", onMove);
  }, [host]);
  return point;
}
